import * as Sentry from "@sentry/react";
import { getSentryFeedbackConfig } from "@/lib/buildInfo";
import { getInitialSentryFeedbackColorScheme } from "@/lib/sentryFeedbackTheme";
import { redactFragmentForTelemetry, redactQueryForTelemetry, redactUrlForTelemetry } from "@/lib/sensitiveUrl";

// ENT-3137 WP1 (VETO C1): public token routes carry a bearer access token in
// the URL query string, and it must never reach Sentry.
// ENT-6550: browserTracingIntegration names each browser.* span after the navigation entry, which
// keeps the URL the browser loaded, so tokens rode out in fields a named list did not cover. Walk
// every string in the outgoing event instead, so a field the SDK adds later is covered too.
const SENTRY_QUERY_FIELDS = new Set(["query_string", "http.query", "url.query"]);
const SENTRY_FRAGMENT_FIELDS = new Set(["http.fragment", "url.fragment"]);
// Internal SDK state, never serialised, and it can hold live scopes.
const SENTRY_UNSENT_FIELDS = new Set(["sdkProcessingMetadata"]);

function scrubSentryString(key: string, value: string): string {
  if (SENTRY_QUERY_FIELDS.has(key)) {
    return redactQueryForTelemetry(value);
  }
  if (SENTRY_FRAGMENT_FIELDS.has(key)) {
    return redactFragmentForTelemetry(value);
  }
  return redactUrlForTelemetry(value);
}

function scrubSentryValue(container: Record<string, unknown> | unknown[], seen: WeakSet<object>): void {
  if (seen.has(container)) {
    return;
  }
  seen.add(container);
  const entries: [string | number, unknown][] = Array.isArray(container)
    ? container.map((value, index) => [index, value])
    : Object.entries(container);
  for (const [key, value] of entries) {
    if (typeof value === "string") {
      (container as Record<string | number, unknown>)[key] = scrubSentryString(String(key), value);
    } else if (value && typeof value === "object" && !SENTRY_UNSENT_FIELDS.has(String(key))) {
      scrubSentryValue(value as Record<string, unknown>, seen);
    }
  }
}

function scrubSensitiveSentryUrls(event: Sentry.Event): void {
  scrubSentryValue(event as Record<string, unknown>, new WeakSet());
}

const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE;
const appVersion = __APP_VERSION__;
const buildRef = __APP_BUILD_REF__;
const appSha = __APP_SHA__;
const release = import.meta.env.VITE_SENTRY_RELEASE || `supplyspark-ent@${appVersion}`;
const sentryFeedbackConfig = getSentryFeedbackConfig();

const replayPrivacyConfig = {
  maskAllText: false,
  maskAllInputs: false,
  blockAllMedia: false,
  mask: [
    "input[type='password']",
    "input[autocomplete='current-password']",
    "input[autocomplete='new-password']",
    "input[autocomplete='one-time-code']",
    "input[type='email']",
    "input[type='tel']",
    "input[name*='password' i]",
    "input[name*='email' i]",
    "input[name*='phone' i]",
    "input[name*='otp' i]",
    "input[name*='mfa' i]",
    "input[name*='token' i]",
    "input[name*='secret' i]",
    "input[name*='api' i]",
    "input[name*='key' i]",
    "textarea[name*='token' i]",
    "textarea[name*='secret' i]",
    "textarea[name*='api' i]",
    "textarea[name*='key' i]",
    "[data-sensitive='true']",
  ],
  ignore: [
    "input[type='password']",
    "input[autocomplete='one-time-code']",
    "input[name*='otp' i]",
    "input[name*='mfa' i]",
    "input[name*='token' i]",
    "input[name*='secret' i]",
    "input[name*='api' i]",
    "input[name*='key' i]",
  ],
};

const defaultSampleRateProfiles = {
  development: {
    tracesSampleRate: 1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,
  },
  preview: {
    tracesSampleRate: 1,
    replaysSessionSampleRate: 0.25,
    replaysOnErrorSampleRate: 1,
  },
  staging: {
    tracesSampleRate: 1,
    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 1,
  },
  production: {
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1,
  },
} as const;

type SampleRateProfile = (typeof defaultSampleRateProfiles)[keyof typeof defaultSampleRateProfiles];

function isLocalSentryEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_LOCAL_SENTRY === "true";
}

function isLocalBrowserRuntime(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function shouldDisableLocalSentry(): boolean {
  if (isLocalSentryEnabled()) {
    return false;
  }

  const normalizedEnvironment = environment.toLowerCase();
  const localLikeEnvironment =
    normalizedEnvironment === "development" || normalizedEnvironment === "local";

  return localLikeEnvironment && isLocalBrowserRuntime();
}

function resolveSampleRateProfile(currentEnvironment: string): SampleRateProfile {
  const normalizedEnvironment = currentEnvironment.toLowerCase();

  return (
    defaultSampleRateProfiles[normalizedEnvironment as keyof typeof defaultSampleRateProfiles] ??
    defaultSampleRateProfiles.production
  );
}

function resolveSampleRate(rawValue: string | undefined, fallback: number): number {
  const parsedValue = Number.parseFloat(rawValue ?? "");
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

export function initSentry(): void {
  if (!dsn || shouldDisableLocalSentry()) {
    return;
  }

  const sampleRateProfile = resolveSampleRateProfile(environment);
  const tracesSampleRate = resolveSampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    sampleRateProfile.tracesSampleRate,
  );
  const replaysSessionSampleRate = resolveSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    sampleRateProfile.replaysSessionSampleRate,
  );
  const replaysOnErrorSampleRate = resolveSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    sampleRateProfile.replaysOnErrorSampleRate,
  );

  Sentry.init({
    dsn,
    environment,
    release,
    initialScope: {
      tags: {
        app_version: appVersion,
        build_ref: buildRef,
        app_sha: appSha,
      },
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(replayPrivacyConfig),
      Sentry.feedbackIntegration({
        autoInject: false,
        showBranding: false,
        colorScheme: getInitialSentryFeedbackColorScheme(),
        ...sentryFeedbackConfig,
      }),
    ],
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.vercel\.app/,
      /^https:\/\/.*\.supabase\.co/,
/^https:\/\/(?:[a-z0-9-]+\.)*supply-guard\.io(?::\d+)?(?:[/?#]|$)/,
    ],
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    beforeSend(event) {
      scrubSensitiveSentryUrls(event);
      return event;
    },
    beforeSendTransaction(event) {
      scrubSensitiveSentryUrls(event);
      return event;
    },
  });
}
